/**
 * IntegrationOrdersSubTab.tsx
 *
 * Generic patient-orders table for a paid lab integration (SiPhox, BodySpec, …).
 * Lists orders with their fulfillment status, billing, amount, and a "results
 * ready" indicator when the vendor has published results (order metadata
 * `resultsAvailable`).
 *
 * Axiom (peptides) is a prescribed order rather than a lab collection, so its
 * table shows "Ordered by" (staff) and "Prescriber" instead of the order date,
 * results, and billing columns.
 */
import { useEffect, useMemo, useState, type ReactNode } from 'react';
import { useQuery } from '@tanstack/react-query';
import { Ban, Bug, CheckCircle2, ChevronDown, ChevronsUpDown, ChevronUp, Copy, CreditCard, Download, ExternalLink, Info, LifeBuoy, Pencil, RefreshCw, SendHorizonal, Truck } from 'lucide-react';
import { Tooltip, TooltipTrigger, TooltipContent } from '@/components/ui/tooltip';
import { Badge } from '@/components/ui/Badge';
import { Modal } from '@/components/ui/Modal';
import { Switch } from '@/components/ui/Switch';
import { Button } from '@/components/ui/Button';
import { downloadOrdersCsv, orderExportFileName } from '@/lib/orderExport';
import { extractPrescriber, orderPrescriber } from '@/lib/orderPrescriber';
import { Spinner } from '@/components/ui';
import { PatientLink } from '@/components/ui/PatientLink';
import type { BadgeVariant } from '@/types/common.types';
import { getPatientIntegrationOrders, ORDER_CANCEL_WINDOW_MINUTES, type IntegrationOrder } from '@/services/integrations.service';
import {
  reorderDate,
  isReorderDue,
  isReorderable,
  reorderCountdown,
  supplyDays,
  supplyIsEstimated,
  computeSupersededBy,
} from '@/lib/peptideOrder';
import { AxiomSupportSection } from './AxiomSupportSection';
import { orderLineName } from './integrationAddOns';
import { MescreenResults, openMescreenPdf } from './MescreenResults';
import { OrderActivity } from './OrderActivity';
import { HandoffChips, HandoffSection, useOrderHandoff, type OrderHandoffControls } from './OrderHandoff';
import { useAuthStore } from '@/store/authStore';
import { AxiomOrderDebugModal } from './AxiomOrderDebugModal';

const CANCEL_WINDOW_MS = ORDER_CANCEL_WINDOW_MINUTES * 60_000;

/**
 * Axiom-family integrations (pens 'axiom' and vials 'axiom_vials') share the
 * same pharmacy pipeline, so both get the prescribed-order treatment: status
 * polling, pharmacy cancellation rules, support tickets, re-order column, and
 * the prescriber-oriented table layout.
 */
const isAxiomFamily = (key: string): boolean => key === 'axiom' || key === 'axiom_vials';

/**
 * Integrations that dispense rather than test, so a "Results" column can never
 * hold anything. It was rendered for every non-Axiom vendor, which swept in the
 * pharmacies: staff read a permanently empty column and asked what was missing.
 */
const hasNoResults = (key: string): boolean => isAxiomFamily(key) || key === 'rmm';
// Statuses past which an order can no longer be cancelled (mirrors the server gate).
const NON_CANCELLABLE = new Set(['cancelled', 'rejected', 'completed', 'delivered', 'shipped', 'ready_to_ship']);

/**
 * Statuses that mean the order never left Prospera — held by the payment gate,
 * or rejected on the way out. Cancelling one is a local bookkeeping act, so it
 * is offered for every integration and never expires.
 */
const NEVER_SENT = new Set(['awaiting_payment', 'failed', 'error', 'agreement_unsigned']);

const BILLING_MODE_LABELS: Record<string, string> = {
  charge_patient: 'Charge patient',
  log_for_settlement: 'Log & settle monthly',
  comp_membership: 'Covered by membership',
};

/** A fulfilment status's badge colour — also the Marketplace Orders page's, so a status looks the same on both. */
export function fulfillmentVariant(status: string | null): BadgeVariant {
  switch (status) {
    case 'completed':
    case 'delivered': return 'success';
    case 'collected':
    case 'kit_delivered':
    case 'kit_shipped':
    case 'shipped':
    case 'ready_to_ship':
    case 'scheduled': return 'info';
    case 'cancelled':
    case 'rejected':
    case 'failed':
    case 'error': return 'error';
    // awaiting_payment / submitting / in-production states → 'warning' (in flight).
    default: return 'warning';
  }
}

/**
 * The carrier to assume when a vendor sends a tracking number without naming one.
 *
 * Axiom ships FedEx by default and their fulfilment webhook frequently omits the
 * carrier, which left the number rendered as bare text with no way to track it.
 * Deliberately per-integration rather than a blanket default: the lab vendors on
 * this same table ship UPS and USPS, and guessing FedEx for them would send staff
 * to a tracking page that will never recognise the number.
 */
const DEFAULT_CARRIER: Record<string, string> = { axiom: 'FedEx', axiom_vials: 'FedEx' };

/** Shipment tracking captured from vendor webhooks (metadata.tracking). */
interface OrderTracking {
  trackingNumber: string;
  carrier?: string;
  /** True when `carrier` is this integration's default rather than something the vendor reported. */
  carrierAssumed?: boolean;
  /** This package's own dates, as the vendor reported them. */
  shippedAt?: string;
  deliveredAt?: string;
}

/**
 * The carrier to show for a package: the one the vendor reported, else this
 * integration's default, else (Partell) the carrier the number's shape names.
 * One rule for the single tracking and for every package.
 */
function resolveCarrier(reported: unknown, integrationKey: string, trackingNumber: string): Pick<OrderTracking, 'carrier' | 'carrierAssumed'> {
  const r = typeof reported === 'string' ? reported.trim() : '';
  if (r !== '') return { carrier: r };
  const assumed = DEFAULT_CARRIER[integrationKey] ?? (integrationKey === 'rmm' ? carrierFromNumber(trackingNumber) : null);
  return assumed ? { carrier: assumed, carrierAssumed: true } : {};
}

/** A package's dates, kept only when they are non-empty text. */
function packageDates(src: Record<string, unknown> | undefined): Pick<OrderTracking, 'shippedAt' | 'deliveredAt'> {
  const out: Pick<OrderTracking, 'shippedAt' | 'deliveredAt'> = {};
  if (typeof src?.shippedAt === 'string' && src.shippedAt.trim() !== '') out.shippedAt = src.shippedAt.trim();
  if (typeof src?.deliveredAt === 'string' && src.deliveredAt.trim() !== '') out.deliveredAt = src.deliveredAt.trim();
  return out;
}

/** "Delivered Oct 12" / "Shipped Oct 10" for one package; '' when it has no date. */
export function packageDateLine(t: Pick<OrderTracking, 'shippedAt' | 'deliveredAt'>): string {
  const fmt = (v: string) => {
    const d = new Date(v);
    return Number.isNaN(d.getTime()) ? '' : d.toLocaleDateString('en-US', { month: 'short', day: 'numeric' });
  };
  if (t.deliveredAt && fmt(t.deliveredAt)) return `Delivered ${fmt(t.deliveredAt)}`;
  if (t.shippedAt && fmt(t.shippedAt)) return `Shipped ${fmt(t.shippedAt)}`;
  return '';
}
/**
 * The carrier a bare tracking number belongs to, from its shape — the same
 * rules as RmmIntegration::carrierFor(). Null when the shape is not
 * distinctive: better no link than a link to the wrong carrier.
 */
export function carrierFromNumber(raw: string): string | null {
  const n = raw.replace(/\s+/g, '').toUpperCase();
  if (/^1Z[0-9A-Z]{16}$/.test(n)) return 'UPS';
  if (/^(9[1-5]\d{18,20}|[A-Z]{2}\d{9}US)$/.test(n)) return 'USPS';
  if (/^(\d{12}|\d{15}|\d{20}|96\d{20})$/.test(n)) return 'FedEx';
  return null;
}

export function extractTracking(
  metadata: Record<string, unknown> | null,
  integrationKey: string,
  items?: IntegrationOrder['items'],
): OrderTracking | null {
  const t = metadata?.tracking as Record<string, unknown> | undefined;
  let num = typeof t?.trackingNumber === 'string' ? t.trackingNumber.trim() : '';
  // Partell keeps a tracking number per script; orders shipped before the
  // order-level record existed carry it only there.
  if (!num && items) {
    const line = items.find((i) => (i.trackingNumber ?? '').trim() !== '');
    num = (line?.trackingNumber ?? '').trim();
  }
  if (!num) return null;
  return { trackingNumber: num, ...resolveCarrier(t?.carrier, integrationKey, num), ...packageDates(t) };
}

/**
 * Every package of an order (SCRUM-1898: products sometimes ship separately).
 * Axiom orders keep a list in metadata.shipments; anything else — and older
 * Axiom orders — has the one tracking above. Each carries the products in it
 * when the vendor named them.
 */
export function extractShipments(
  metadata: Record<string, unknown> | null,
  integrationKey: string,
  items?: IntegrationOrder['items'],
): Array<OrderTracking & { items?: string[] }> {
  const list = Array.isArray(metadata?.shipments) ? (metadata!.shipments as Array<Record<string, unknown>>) : [];
  const shipments = list
    .filter((s) => typeof s?.trackingNumber === 'string' && (s.trackingNumber as string).trim() !== '')
    .map((s) => {
      const num = (s.trackingNumber as string).trim();
      return {
        trackingNumber: num,
        ...resolveCarrier(s.carrier, integrationKey, num),
        ...packageDates(s),
        ...(Array.isArray(s.items) && s.items.length > 0 ? { items: (s.items as unknown[]).map(String) } : {}),
      };
    });
  if (shipments.length > 0) return shipments;
  const single = extractTracking(metadata, integrationKey, items);
  return single ? [single] : [];
}

/** Carrier tracking page for the shipment, when the carrier is recognized. */
function trackingUrl(t: OrderTracking): string | null {
  const c = (t.carrier ?? '').toUpperCase();
  const n = encodeURIComponent(t.trackingNumber);
  if (c.includes('UPS')) return `https://www.ups.com/track?tracknum=${n}`;
  if (c.includes('USPS')) return `https://tools.usps.com/go/TrackConfirmAction?tLabels=${n}`;
  if (c.includes('FEDEX')) return `https://www.fedex.com/fedextrack/?trknbr=${n}`;
  if (c.includes('DHL')) return `https://www.dhl.com/us-en/home/tracking.html?tracking-id=${n}`;
  return null;
}

/** Pull the vendor failure reason from order metadata (e.g. axiomError, siphoxError). */
function extractError(metadata: Record<string, unknown> | null): string | null {
  if (!metadata) return null;
  for (const [k, v] of Object.entries(metadata)) {
    if (k.toLowerCase().endsWith('error') && typeof v === 'string' && v.trim() !== '') return v;
  }
  return null;
}

function money(amount: number, currency: string): string {
  return new Intl.NumberFormat('en-US', { style: 'currency', currency: currency || 'USD' }).format(amount);
}

function DetailRow({ label, children }: { label: string; children: ReactNode }) {
  return (
    <div className="flex justify-between gap-6 py-1 text-sm">
      <span className="shrink-0 text-slate-500">{label}</span>
      <span className="text-right text-slate-900">{children}</span>
    </div>
  );
}

function Section({ title, children }: { title: string; children: ReactNode }) {
  return (
    <div>
      <h4 className="mb-1 text-xs font-semibold uppercase tracking-wide text-slate-500">{title}</h4>
      <div className="rounded-lg border border-slate-200 bg-slate-50/60 px-3 py-2">{children}</div>
    </div>
  );
}

/** Protocol details for one ordered item, from its captured product metadata. */
function ProtocolDetails({ pm }: { pm: Record<string, unknown> | null | undefined }) {
  if (!pm) return null;
  const str = (k: string) => (typeof pm[k] === 'string' && (pm[k] as string).trim() ? (pm[k] as string) : null);
  const num = (k: string) => (typeof pm[k] === 'number' ? (pm[k] as number) : null);
  const rows: Array<[string, ReactNode]> = [];
  if (str('strength')) rows.push(['Concentration', str('strength')]);
  if (str('fillSize')) rows.push(['Fill / size', str('fillSize')]);
  if (str('totalMg')) rows.push(['Total mg', str('totalMg')]);
  if (str('recommendedDose')) rows.push(['Recommended dose', str('recommendedDose')]);
  if (str('frequency')) rows.push(['Frequency', str('frequency')]);
  if (num('injectionsPerCart') != null) {
    const weeks = num('weeksPerCart');
    rows.push(['Injections/cartridge', `${num('injectionsPerCart')}${weeks ? ` (≈ ${weeks} weeks)` : ''}`]);
  }
  if (num('budDays') != null) rows.push(['Beyond-use date', `${num('budDays')} days`]);
  if (rows.length === 0) return null;
  return (
    <div className="mt-1 rounded-md border border-blue-100 bg-blue-50 px-2.5 py-1.5 text-[11px] text-blue-800">
      {rows.map(([label, val]) => (
        <p key={label}><span className="font-medium">{label}:</span> {val}</p>
      ))}
    </div>
  );
}

/** Full order details, rendered from the order's existing data (no extra fetch). */
function OrderDetailsModal({
  order, fulfillmentLabels, integrationKey, patientId, handoff,
}: {
  order: IntegrationOrder; fulfillmentLabels: Record<string, string>; integrationKey: string; patientId: number;
  /** Axiom only: the clinic-handoff controls shared with the table's chips (SCRUM-2200). */
  handoff?: OrderHandoffControls;
}) {
  const meta = (order.metadata ?? {}) as Record<string, unknown>;
  const lineItems = (meta.lineItems as Array<{
    name: string; quantity: number; unitPrice?: number; integrationProductId?: number;
    productMetadata?: Record<string, unknown> | null;
    /** Practice brand name snapshotted at order time; only the named pens have one. */
    brandName?: string;
  }> | undefined) ?? [];
  const phys = meta.physician as Record<string, unknown> | undefined;
  const addr = meta.shippingAddress as Record<string, unknown> | undefined;
  const addOn = meta.addOn as { penQty?: number; penProvided?: boolean; needleQty?: number; extraNeedleQty?: number; extraNeedles?: boolean } | undefined;
  const dirs = (meta.itemDirections as Record<string, string> | undefined) ?? {};
  const shippingPrice = (meta.shipping as { price?: number } | undefined)?.price;
  const itemsTotal = typeof meta.itemsTotal === 'number' ? (meta.itemsTotal as number) : undefined;
  const shipments = extractShipments(order.metadata, integrationKey, order.items);
  const err = extractError(order.metadata);
  const prescriber = extractPrescriber(order.metadata);
  const cur = order.currency || 'USD';
  const physStr = (k: string) => (typeof phys?.[k] === 'string' ? (phys[k] as string) : '');

  return (
    <div className="space-y-4">
      {/* Status + when */}
      <div className="flex items-center justify-between gap-3">
        <Badge variant={fulfillmentVariant(order.fulfillmentStatus)}>
          {fulfillmentLabels[order.fulfillmentStatus ?? ''] ?? (order.fulfillmentStatus ?? order.status)}
        </Badge>
        <span className="text-sm text-slate-500">
          {order.createdAt
            ? new Date(order.createdAt).toLocaleString('en-US', { month: 'short', day: 'numeric', year: 'numeric', hour: 'numeric', minute: '2-digit' })
            : '—'}
        </span>
      </div>

      {err && (
        <p className="rounded-lg border border-red-200 bg-red-50 px-3 py-2 text-sm text-red-700">{err}</p>
      )}

      <Section title="Items">
        {lineItems.length > 0 ? (
          <ul className="space-y-1.5">
            {lineItems.map((li, i) => {
              const dir = dirs[String((li as { integrationProductId?: number }).integrationProductId ?? '')];
              return (
                <li key={i} className="text-sm">
                  <div className="flex justify-between gap-4">
                    <span className="text-slate-800">
                      {li.quantity}× {li.brandName ? `${li.brandName} — ${li.name}` : orderLineName(li.name, li.productMetadata as { category?: unknown } | undefined)}
                    </span>
                    {typeof li.unitPrice === 'number' && (
                      <span className="text-slate-600">{money(li.unitPrice * li.quantity, cur)}</span>
                    )}
                  </div>
                  {dir && <p className="text-[11px] italic text-slate-500">Directions: {dir}</p>}
                  <ProtocolDetails pm={li.productMetadata} />
                </li>
              );
            })}
          </ul>
        ) : (
          order.items && order.items.length ? (
            // The order's own lines, with what the PHARMACY was told about each
            // one. productName is the catalog placeholder — for a prescription
            // integration that is the vendor's name, so the panel used to
            // describe a script as "Partell (RequestMyMeds)".
            <div className="space-y-2">
              {order.items.map((x, i) => {
                // Only what this line actually carries: a lab kit has no
                // refills, and printing "Refills: —" on one would invent a
                // field the vendor was never sent.
                const specs: Array<[string, string]> = [];
                if (x.refills) specs.push(['Refills', x.refills]);
                if (x.daySupply) specs.push(['Day supply', x.daySupply]);
                if (x.icd10) specs.push(['ICD-10', x.icd10]);
                if (x.dispenseAsWritten) {
                  specs.push(['Dispense as written', x.dispenseAsWritten === 'Yes' ? 'Yes' : 'No']);
                }

                return (
                  <div key={i} className="rounded-md border border-slate-100 bg-slate-50 px-2 py-1.5">
                    <p className="text-sm font-medium text-slate-700">
                      {x.quantity}× {x.name}
                      {x.status ? (
                        <span className="ml-2 text-[11px] font-normal text-slate-500">({x.status})</span>
                      ) : null}
                    </p>
                    {x.sig ? <p className="text-xs italic text-slate-600">{x.sig}</p> : null}
                    {specs.length > 0 && (
                      <p className="mt-0.5 text-[11px] text-slate-500">
                        {specs.map(([k, v]) => `${k}: ${v}`).join(' · ')}
                      </p>
                    )}
                    {x.trackingNumber ? (
                      <p className="text-[11px] text-slate-500">Tracking: {x.trackingNumber}</p>
                    ) : null}
                  </div>
                );
              })}
              {/* Shipping is one decision for the order, so it is stated once
                  rather than repeated on every line. */}
              {(() => {
                const first = order.items?.[0];
                if (!first?.shipType) return null;
                const dest = first.shipType === 'ship_to_clinic' ? 'the clinic' : 'the patient';
                const rate = first.shipRate === 'overnight' ? 'overnight' : '2-day';

                return (
                  <p className="text-[11px] text-slate-500">Ships to {dest}, {rate}.</p>
                );
              })()}
            </div>
          ) : (
            <p className="text-sm text-slate-600">{order.productName ?? '—'}</p>
          )
        )}
        {(() => {
          // New orders store penQty/needleQty; older ones penProvided/extraNeedles(+extraNeedleQty).
          const pen = addOn?.penQty ?? (addOn?.penProvided ? 1 : 0);
          const needles = addOn?.needleQty ?? addOn?.extraNeedleQty ?? (addOn?.extraNeedles ? 1 : 0);
          if (!pen && !needles) return null;
          return (
            <p className="mt-2 border-t border-slate-200 pt-2 text-[11px] text-slate-500">
              Supplies: {[
                pen ? `reusable injector pen ×${pen}` : null,
                needles ? `extra needle packs ×${needles}` : null,
              ].filter(Boolean).join(', ')}
            </p>
          );
        })()}
      </Section>

      <Section title="Amount">
        {itemsTotal != null && <DetailRow label="Items">{money(itemsTotal, cur)}</DetailRow>}
        {shippingPrice != null && shippingPrice > 0 && <DetailRow label="Shipping">{money(shippingPrice, cur)}</DetailRow>}
        <div className="mt-1 flex justify-between border-t border-slate-200 pt-1 text-sm font-semibold text-slate-900">
          <span>Total</span><span>{money(order.amount, cur)}</span>
        </div>
      </Section>

      {(prescriber || phys) && (
        <Section title="Prescriber">
          <DetailRow label="Name">{prescriber ?? '—'}{physStr('credential') ? `, ${physStr('credential')}` : ''}</DetailRow>
          {physStr('npi') && <DetailRow label="NPI">{physStr('npi')}</DetailRow>}
          {physStr('dea') && <DetailRow label="DEA">{physStr('dea')}</DetailRow>}
          {physStr('phone') && <DetailRow label="Phone">{physStr('phone')}</DetailRow>}
        </Section>
      )}

      {addr && (
        <Section title="Ship to">
          <div className="text-sm text-slate-800">
            {typeof addr.recipientName === 'string' && addr.recipientName && <div>{addr.recipientName}</div>}
            <div>{[addr.line1, addr.line2].filter(Boolean).join(', ')}</div>
            <div>{[addr.city, addr.state, addr.zip].filter(Boolean).join(', ')}</div>
            {typeof addr.phone === 'string' && addr.phone && <div className="text-slate-500">{addr.phone}</div>}
          </div>
        </Section>
      )}

      {shipments.length > 0 && (
        <Section title={shipments.length > 1 ? `Tracking — ${shipments.length} packages` : 'Tracking'}>
          <div className="space-y-1.5">
            {shipments.map((t) => {
              const url = trackingUrl(t);
              return (
                <div key={t.trackingNumber}>
                  <p className="flex items-center gap-1.5 text-sm text-slate-700">
                    <Truck className="h-3.5 w-3.5 shrink-0" />
                    <span title={t.carrierAssumed ? `Carrier not reported by the vendor; ${t.carrier} is the default for this integration.` : undefined}>
                      {t.carrier ? `${t.carrier} ` : ''}{t.trackingNumber}
                    </span>
                    {url && (
                      <a href={url} target="_blank" rel="noreferrer" className="inline-flex items-center gap-0.5 text-blue-600 hover:underline">
                        Track <ExternalLink className="h-3 w-3" />
                      </a>
                    )}
                  </p>
                  {packageDateLine(t) && <p className="ml-5 text-xs text-slate-500">{packageDateLine(t)}</p>}
                  {t.items && t.items.length > 0 && (
                    <p className="ml-5 text-xs text-slate-500">{t.items.join(', ')}</p>
                  )}
                </div>
              );
            })}
          </div>
        </Section>
      )}

      {integrationKey === 'mescreen' && (meta.mescreenBarcode || meta.mescreenResults) ? (
        <Section title="Results">
          <MescreenResults patientId={patientId} orderId={order.integrationOrderId} meta={meta} />
        </Section>
      ) : null}

      {handoff && (
        <Section title="Clinic handoff">
          <HandoffSection order={order} controls={handoff} />
        </Section>
      )}

      <Section title="Activity">
        <OrderActivity patientId={patientId} orderId={order.integrationOrderId} />
      </Section>

      <div className="flex justify-between text-xs text-slate-400">
        <span>Placed by {order.orderedBy ?? '—'}</span>
        <span>Order #{order.integrationOrderId}</span>
      </div>
    </div>
  );
}

export interface IntegrationOrdersSubTabProps {
  patientId: number;
  integrationKey: string;
  /** Maps fulfillmentStatus → human label (vendor-specific lifecycle). */
  fulfillmentLabels: Record<string, string>;
  emptyMessage: string;
  /** When provided, renders an Edit button per row (re-opens the order form). */
  onEdit?: (order: IntegrationOrder) => void;
  /**
   * Place this order again AS IT WAS — same items, same shipping address.
   * Distinct from `onReorder`, which is the refill and lets the ship-to come
   * from the patient's own addresses.
   */
  onClone?: (order: IntegrationOrder) => void;
  /**
   * Re-send a CANCELLED order (shown on cancelled rows only). Same as a clone
   * but records which cancelled order it replaces; falls back to onClone.
   */
  onResend?: (order: IntegrationOrder) => void;
  /**
   * Plain-language explanation per fulfillment status (keyed by status code).
   * When set, the Status column header gets an info icon listing every status
   * and what it means.
   */
  statusHelp?: Record<string, string>;
  /**
   * When provided, renders a Cancel button per row — but only within
   * ORDER_CANCEL_WINDOW_MINUTES of the order's creation and while it's still
   * cancellable. The server enforces the same window authoritatively.
   */
  onCancel?: (order: IntegrationOrder) => void;
  /**
   * When provided, renders a "Finish payment" button on orders parked in
   * 'awaiting_payment' — the practice requires payment up front, so the order
   * is held server-side and is NOT sent to the vendor until the fee is
   * collected. Collecting it releases the order into the normal grace window.
   */
  onFinishPayment?: (order: IntegrationOrder) => void;
  /**
   * When provided, renders a "Re-order" button on every non-cancelled row —
   * any past order can be placed again, not only one whose supply has run down.
   * Rows that ARE due get the amber emphasis (and the running-low wording) so
   * the urgent ones still stand out. Re-opens the order flow prefilled from the
   * order. Axiom only.
   */
  onReorder?: (order: IntegrationOrder) => void;
  /**
   * When provided, renders a "Retry" button on failed orders that never reached
   * the pharmacy. Deliberately NOT the same action as Re-order: retry re-sends
   * THIS order — same items, same charge, no new order row — after whatever
   * rejected it was fixed, while Re-order opens the order flow to place a fresh
   * one (typically at a new dosage). Different icon for the same reason.
   */
  onRetry?: (order: IntegrationOrder) => void;
  /**
   * Pre-fetched orders to render instead of fetching per-patient. Set by the
   * global Peptides page (account-wide list). When omitted, the component
   * fetches this patient's orders itself.
   */
  orders?: IntegrationOrder[];
  /**
   * The page's UNFILTERED list, for deciding which rows were already
   * re-ordered (computeSupersededBy). `orders` may be filtered (to completed,
   * say) and so miss the newer order that re-ordered a row; judged from
   * `orders` alone, that row would offer Re-order again. Omitted: `orders`.
   */
  allOrders?: IntegrationOrder[];
  /**
   * Hide cancelled orders by default behind a "Show cancelled" switch above
   * the table (default off = cancelled hidden). Peptides surfaces only — lab
   * integrations keep their historical show-everything behavior.
   */
  hideCancelled?: boolean;
  /** Render a "Patient" column (account-wide list). Requires row.patientId/Name. */
  showPatient?: boolean;
  /**
   * Open this order's Axiom support panel on arrival — a support alert links
   * here (SCRUM-1994). Taken as the order itself, so it opens even when the
   * page's filters hide its row. `onSupportOpened` lets the page clear it.
   */
  openSupportFor?: IntegrationOrder | null;
  /** With openSupportFor: open this ticket's thread (axiomSupportTicketId). */
  openSupportTicketId?: number | null;
  onSupportOpened?: () => void;
  /**
   * Open this order's details on arrival — a link straight to the order
   * (linkedOrder.ts). Opens once the list has loaded; `onOrderOpened` is called
   * then, found or not, so the page can take the link out of the URL.
   */
  openOrderId?: number | null;
  onOrderOpened?: () => void;
  /**
   * Show an "Export CSV" button above the table (SCRUM-2205). It downloads the
   * rows exactly as shown — filtered by the page, sorted by the headers, with
   * or without cancelled per the switch. `facilityName` labels the Facility
   * column, since rows carry only the id.
   */
  exportAs?: { filePrefix: string; facilityName: (facilityId: number | null | undefined) => string };
  /**
   * Render a "Source" column — which client placed the order ('webapp' |
   * 'mobile' | 'nexus'), from row.source. Opt-in because only the App Orders
   * page is about the ordering channel; the patient and Peptides tabs are not.
   *
   * A row with no source is UNTAGGED (placed before the column existed, or by a
   * client that does not send X-Client yet) and renders as an em dash. It is
   * deliberately NOT labelled — calling it 'webapp', 'other' or 'unknown' would
   * put a channel name on data nobody observed.
   */
  showSource?: boolean;
}

/** An order held by the server-side payment gate — nothing was sent to the vendor. */
function isAwaitingPayment(order: IntegrationOrder): boolean {
  return order.fulfillmentStatus === 'awaiting_payment';
}

/** Fulfillment states the server will re-send from (RETRYABLE_FULFILLMENT_STATUSES). */
const RETRYABLE = new Set(['failed', 'error', 'agreement_unsigned', 'rejected']);

/**
 * A failed order that never reached the pharmacy, so it can be pushed again as
 * itself. `externalRef` is the guard that matters: it is set the moment the
 * vendor accepts an order, and retrying past that point would place the same
 * prescription twice. The server enforces this too — the button only avoids
 * offering an action that would be refused.
 */
function canRetry(order: IntegrationOrder): boolean {
  return RETRYABLE.has(order.fulfillmentStatus ?? '') && !order.externalRef;
}

/**
 * An order the pharmacy has not seen, so amending it here amends the thing that
 * will actually be filled (SCRUM-1814 — staff asked to correct the shipping
 * address on a held order before releasing the payment).
 *
 * `externalRef` is the guard, exactly as for Retry: it is set the moment the
 * vendor accepts an order, and nothing changed afterwards is sent to them. An
 * address "corrected" past that point would read as fixed on our screen while
 * the package went to the old one — worse than no edit button at all. The
 * server refuses it too; this only avoids offering an action that would fail.
 */
/**
 * An order worth repeating verbatim: it carries a shipping address of its own.
 *
 * Without one there is nothing to clone that a plain re-order would not already
 * do, and the button would promise a fidelity it cannot deliver. Cancelled
 * orders get "Re-send" instead, which is the same clone under the name staff
 * use for bringing a cancelled order back.
 */
function canClone(order: IntegrationOrder): boolean {
  const meta = order.metadata as { shippingAddress?: { line1?: string } } | null | undefined;
  return !isCancelled(order) && !!meta?.shippingAddress?.line1;
}

function canEdit(order: IntegrationOrder): boolean {
  return NEVER_SENT.has(order.fulfillmentStatus ?? '') && !order.externalRef;
}

/**
 * Compact icon-only row action with a hover tooltip. The accessible name
 * (aria-label) is the action's old button text, so screen readers and tests
 * see the same names as before the icon conversion.
 */
function IconAction({ label, tip, onClick, tone = 'default', children }: {
  label: string;
  tip: string;
  onClick: () => void;
  /**
   * 'danger' renders the destructive (red) treatment — used by Cancel.
   * 'attention' renders the amber treatment that flags a time-sensitive action
   * (a re-order whose supply has run down), matching the Re-order date cell.
   */
  tone?: 'default' | 'danger' | 'attention';
  children: ReactNode;
}) {
  return (
    <Tooltip>
      <TooltipTrigger asChild>
        <button
          type="button"
          aria-label={label}
          onClick={onClick}
          className={`inline-flex h-8 w-8 shrink-0 items-center justify-center rounded-md border transition-colors focus-visible:outline-none focus-visible:ring-1 focus-visible:ring-blue-500 ${
            tone === 'danger'
              ? 'border-red-200 text-red-600 hover:bg-red-50 hover:text-red-700'
              : tone === 'attention'
                ? 'border-amber-300 bg-amber-50 text-amber-700 hover:bg-amber-100 hover:text-amber-800'
                : 'border-slate-200 text-slate-600 hover:bg-slate-100 hover:text-slate-900'
          }`}
        >
          {children}
        </button>
      </TooltipTrigger>
      <TooltipContent className="max-w-[260px]">{tip}</TooltipContent>
    </Tooltip>
  );
}

/**
 * A cancelled order. Staff cancels set BOTH fields to 'cancelled'
 * (IntegrationsController@cancelOrder); vendor webhooks may move only
 * fulfillmentStatus — either one means cancelled. Deliberately excludes
 * 'rejected' / 'failed' / 'error', which staff still need to see and act on.
 */
function isCancelled(order: IntegrationOrder): boolean {
  return order.fulfillmentStatus === 'cancelled' || order.status === 'cancelled';
}

/* ------------------------------------------------------------------ *
 * Column sorting
 *
 * Sorting is opt-in: the state starts null and the rows render in the
 * order the API/parent supplied them (integrationOrderId DESC) until a
 * header is clicked. Only columns that exist in the current branch get a
 * sortable header — the lab mounts have no Re-order/Patient column, so
 * those keys are never reachable there.
 * ------------------------------------------------------------------ */

type SortKey = 'orderNumber' | 'patient' | 'ordered' | 'product' | 'orderedBy' | 'prescriber' | 'status' | 'amount' | 'supply' | 'reorder';
type SortDir = 'asc' | 'desc';
/** '' means "no value" — those rows sort last in BOTH directions. */
type SortValue = string | number;

/** Keys whose most useful first click is newest-first. */
const DESC_FIRST_KEYS = new Set<SortKey>(['ordered', 'reorder']);

function timeValue(iso: string | null | undefined): SortValue {
  if (!iso) return '';
  const t = new Date(iso).getTime();
  return Number.isNaN(t) ? '' : t;
}

/** The product text the Product cell displays, flattened for comparison. */
function productSortValue(order: IntegrationOrder): SortValue {
  const meta = order.metadata as Record<string, unknown> | null;
  // NOT kitType — see the Product cell: that is SiPhox's vendor code, not a
  // product name, and sorting by it would order "Core Health" under E.
  const realName = (meta?.genovaProductName ?? meta?.skuName) as string | undefined;
  if (typeof realName === 'string' && realName.trim()) return realName;
  const li = meta?.lineItems as Array<{ name: string; quantity: number; brandName?: string }> | undefined;
  if (li && li.length) return li.map((x) => (x.brandName ? `${x.brandName} — ${x.name}` : x.name)).join(', ');
  return order.productName ?? '';
}

/**
 * Sort key → the value to compare. Two of these are DERIVED rather than row
 * fields, so indexing the order object directly would not work: the re-order
 * date is computed by reorderDate(), and Status must sort by the label the
 * cell displays, not the raw fulfillmentStatus code.
 */
const SORT_ACCESSORS: Record<SortKey, (o: IntegrationOrder, labels: Record<string, string>) => SortValue> = {
  orderNumber: (o) => o.integrationOrderId,
  patient: (o) => o.patientName?.trim() || (o.patientId ? `#${o.patientId}` : ''),
  ordered: (o) => timeValue(o.createdAt),
  product: (o) => productSortValue(o),
  orderedBy: (o) => o.orderedBy?.trim() ?? '',
  prescriber: (o) => orderPrescriber(o) ?? '',
  status: (o, labels) => labels[o.fulfillmentStatus ?? ''] ?? o.fulfillmentStatus ?? o.status ?? '',
  amount: (o) => o.amount,
  supply: (o) => supplyDays(o),
  reorder: (o) => {
    const d = reorderDate(o);
    return d ? d.getTime() : '';
  },
};

/**
 * "When do I re-order?" in words. The column used to show only a date, which
 * left the ECCs subtracting today's date by hand for every row — the whole
 * reason they kept the spreadsheet.
 */
function reorderPhrase(daysAway: number): string {
  if (daysAway === 0) return 'Due today';
  if (daysAway === 1) return 'Due tomorrow';
  if (daysAway > 1) return `in ${daysAway} days`;
  if (daysAway === -1) return '1 day overdue';
  return `${Math.abs(daysAway)} days overdue`;
}

function compareSortValues(a: SortValue, b: SortValue): number {
  if (typeof a === 'number' && typeof b === 'number') return a - b;
  return String(a).localeCompare(String(b), undefined, { numeric: true, sensitivity: 'base' });
}

function ariaSortFor(active: boolean, dir: SortDir): 'ascending' | 'descending' | 'none' {
  if (!active) return 'none';
  return dir === 'asc' ? 'ascending' : 'descending';
}

/**
 * The clickable part of a sortable header. Kept separate from SortableHeader
 * so the Status column can render it as a SIBLING of its info-icon tooltip
 * trigger — nesting that <button> inside this one would be invalid DOM and
 * would fire the sort when the info icon is clicked.
 */
function SortButton({ label, sortKey, sortBy, sortDir, onSort }: {
  label: string;
  sortKey: SortKey;
  sortBy: SortKey | null;
  sortDir: SortDir;
  onSort: (key: SortKey) => void;
}) {
  const active = sortBy === sortKey;
  return (
    <button
      type="button"
      onClick={() => onSort(sortKey)}
      className={`group inline-flex items-center gap-1 uppercase tracking-wide transition-colors ${
        active ? 'text-slate-700' : 'text-slate-500 hover:text-slate-700'
      }`}
    >
      {label}
      {active ? (
        sortDir === 'asc' ? <ChevronUp className="h-3.5 w-3.5" /> : <ChevronDown className="h-3.5 w-3.5" />
      ) : (
        <ChevronsUpDown className="h-3.5 w-3.5 text-slate-300 group-hover:text-slate-400" />
      )}
    </button>
  );
}

function SortableHeader({ label, sortKey, sortBy, sortDir, onSort, className }: {
  label: string;
  sortKey: SortKey;
  sortBy: SortKey | null;
  sortDir: SortDir;
  onSort: (key: SortKey) => void;
  className?: string;
}) {
  return (
    <th className={`px-3 py-2 ${className ?? ''}`} aria-sort={ariaSortFor(sortBy === sortKey, sortDir)}>
      <SortButton label={label} sortKey={sortKey} sortBy={sortBy} sortDir={sortDir} onSort={onSort} />
    </th>
  );
}

export function IntegrationOrdersSubTab({ patientId, integrationKey, fulfillmentLabels, emptyMessage, onEdit, onClone, onResend, onCancel, onFinishPayment, onReorder, onRetry, orders: ordersProp, allOrders, showPatient, showSource, statusHelp, hideCancelled, openSupportFor, openSupportTicketId, onSupportOpened, openOrderId, onOrderOpened, exportAs }: IntegrationOrdersSubTabProps) {
  // Axiom: cancellation is STATUS-based (the pharmacy allows it only while an
  // order is received/routed) and the statuses advance quickly — poll while any
  // order is still in flight so the Cancel button and badges track reality.
  const isAxiom = isAxiomFamily(integrationKey);
  // Ordered by / Prescriber / Day's supply / Re-order: the pharmacy columns,
  // shared by Axiom and Partell (SCRUM-2121). Vendor and Clinic stay Axiom's.
  const showRxColumns = isAxiom || integrationKey === 'rmm';
  const noResults = hasNoResults(integrationKey);
  const AXIOM_CANCELLABLE = new Set(['scheduled', 'received', 'routed']);
  const AXIOM_IN_FLIGHT = new Set(['scheduled', 'submitting', 'received', 'routed', 'in_production', 'ready_to_pack', 'ready_to_ship', 'shipped']);

  // When the parent supplies orders (global page), render those and skip the
  // per-patient fetch. Otherwise fetch this patient's orders.
  const { data: fetched = [], isLoading: isFetching } = useQuery({
    queryKey: [`patient-${integrationKey}-orders`, patientId],
    queryFn: () => getPatientIntegrationOrders(patientId, integrationKey),
    staleTime: 30_000,
    enabled: ordersProp === undefined,
    refetchInterval: (query) => {
      if (!isAxiom) return false;
      const list = (query.state.data ?? []) as IntegrationOrder[];
      return list.some((o) => AXIOM_IN_FLIGHT.has(o.fulfillmentStatus ?? '')) ? 15_000 : false;
    },
  });
  const orders = ordersProp ?? fetched;
  const isLoading = ordersProp === undefined && isFetching;
  // integrationOrderId -> the id of the order that already re-ordered it
  // (same patient, same dosed product, newer, not cancelled). That order's
  // own overdue math stays live; this one shows "Reordered" instead.
  const chainOrders = allOrders ?? orders;
  const supersededBy = useMemo(
    () => computeSupersededBy(chainOrders, (o) => o.patientId ?? patientId),
    [chainOrders, patientId],
  );
  // The re-order may sit outside the filtered rows, so look it up in the full list.
  const chainOrderById = useMemo(
    () => new Map(chainOrders.map((o) => [o.integrationOrderId, o])),
    [chainOrders],
  );
  const orderById = useMemo(
    () => new Map(orders.map((o) => [o.integrationOrderId, o])),
    [orders],
  );
  // Clinic handoff chips (SCRUM-2200): optimistic toggles shared by the rows
  // and the detail modal.
  const handoff = useOrderHandoff(orders, patientId);

  // The order whose full details are shown in the modal (null = closed).
  const [detailOrder, setDetailOrder] = useState<IntegrationOrder | null>(null);
  // The order whose Axiom support tickets are shown in their own modal
  // (separate from the order-details modal — not nested inside it).
  const [supportOrder, setSupportOrder] = useState<IntegrationOrder | null>(null);
  const [supportTicketId, setSupportTicketId] = useState<number | null>(null);
  useEffect(() => {
    if (!openSupportFor) return;
    setSupportOrder(openSupportFor);
    setSupportTicketId(openSupportTicketId ?? null);
    onSupportOpened?.();
  }, [openSupportFor, openSupportTicketId, onSupportOpened]);
  // Taken from all orders, not the visible rows, so a cancelled one opens too.
  useEffect(() => {
    if (!openOrderId || isLoading) return;
    const linked = orders.find((o) => o.integrationOrderId === openOrderId);
    if (linked) setDetailOrder(linked);
    onOrderOpened?.();
  }, [openOrderId, isLoading, orders, onOrderOpened]);

  // hideCancelled surfaces: cancelled orders are hidden until this is switched
  // on (session-local, defaults off on every mount).
  const [showCancelled, setShowCancelled] = useState(false);
  const cancelledCount = hideCancelled ? orders.filter(isCancelled).length : 0;
  const visibleOrders = hideCancelled && !showCancelled
    ? orders.filter((o) => !isCancelled(o))
    : orders;

  // Column sort (session-local, like showCancelled). null = untouched, which
  // renders the rows exactly as supplied.
  const [sort, setSort] = useState<{ key: SortKey; dir: SortDir } | null>(null);
  const toggleSort = (key: SortKey) => {
    setSort((cur) =>
      cur && cur.key === key
        ? { key, dir: cur.dir === 'asc' ? 'desc' : 'asc' }
        : { key, dir: DESC_FIRST_KEYS.has(key) ? 'desc' : 'asc' },
    );
  };
  const sortBy = sort?.key ?? null;
  const sortDir = sort?.dir ?? 'asc';

  // Sorting runs AFTER the cancelled filter, so the hidden-count above is
  // unaffected by it.
  const sortedOrders = useMemo(() => {
    if (!sort) return visibleOrders;
    const accessor = SORT_ACCESSORS[sort.key];
    return [...visibleOrders].sort((a, b) => {
      const av = accessor(a, fulfillmentLabels);
      const bv = accessor(b, fulfillmentLabels);
      const aEmpty = av === '';
      const bEmpty = bv === '';
      // Empty values always sort last, regardless of direction.
      if (aEmpty || bEmpty) return aEmpty && bEmpty ? 0 : aEmpty ? 1 : -1;
      const cmp = compareSortValues(av, bv);
      return sort.dir === 'asc' ? cmp : -cmp;
    });
  }, [visibleOrders, sort, fulfillmentLabels]);

  // "Show cancelled" switch — rendered only when there is something to toggle.
  const showToggle = hideCancelled && cancelledCount > 0;
  const cancelledToggle = showToggle || exportAs ? (
    <div className="flex items-center justify-end gap-4 pb-2">
      {showToggle && (
        <label className="flex cursor-pointer items-center gap-2 text-sm text-slate-600">
          <Switch checked={showCancelled} onChange={setShowCancelled} />
          Show cancelled ({cancelledCount})
        </label>
      )}
      {exportAs && (
        <Button
          size="sm"
          variant="secondary"
          leftIcon={<Download className="h-4 w-4" />}
          disabled={sortedOrders.length === 0}
          onClick={() =>
            downloadOrdersCsv(
              sortedOrders,
              {
                statusLabels: fulfillmentLabels,
                facilityName: exportAs.facilityName,
                // Axiom: the order's physician. Partell: each script names its
                // own prescriber, and they can differ — every one is listed.
                prescriber: (o) => orderPrescriber(o) ?? '',
                // Every package, as the table lists them (a split shipment has several).
                tracking: (o) => extractShipments(o.metadata as Record<string, unknown> | null, integrationKey, o.items)
                  .map((t) => t.trackingNumber).filter(Boolean).join('; '),
              },
              orderExportFileName(exportAs.filePrefix),
            )
          }
        >
          Export CSV ({sortedOrders.length})
        </Button>
      )}
    </div>
  ) : null;

  // Tick while any cancel window is open so the button disappears at the deadline
  // without a manual refresh.
  /**
   * Order whose Axiom payload is open for inspection.
   *
   * Admin-only (userLevel >= 3), matching the server. A misdelivered order is
   * billed to the clinic either way, so the people who carry that cost are the
   * ones who need to see where it is going — but the panel names the clinic's
   * own legal details, which clinical staff have no reason to read.
   */
  const [debugOrder, setDebugOrder] = useState<IntegrationOrder | null>(null);
  const userLevel = useAuthStore((s) => s.user?.userLevel ?? 0);
  const canDebug = userLevel >= 3 && isAxiom;

  const [now, setNow] = useState(() => Date.now());
  useEffect(() => {
    if (!onCancel) return;
    const id = setInterval(() => setNow(Date.now()), 15_000);
    return () => clearInterval(id);
  }, [onCancel]);

  const canCancel = (o: IntegrationOrder): boolean => {
    if (!onCancel) return false;
    const fs = o.fulfillmentStatus ?? '';

    // NOTHING WAS SENT — always cancellable, for every integration.
    //
    // This reasoning used to sit inside the isAxiom branch, but it is not about
    // Axiom: an order parked awaiting payment, or one that failed on the way
    // out, never reached the vendor at all, so there is nothing out there to
    // recall and no deadline to respect. Leaving it to the 10-minute window
    // below meant a SiPhox or Partell order stuck at awaiting_payment lost its
    // Cancel button ten minutes after it was raised and could never be closed —
    // reported on both.
    if (NEVER_SENT.has(fs)) return true;

    if (isAxiom) {
      // Pharmacy rule: once it IS at Axiom, cancellable only while
      // received/routed — after that the compounding has begun.
      return AXIOM_CANCELLABLE.has(fs);
    }
    // Partell has no cancel endpoint, so there is no vendor window to race —
    // the server (cancelOrder) refuses only shipped/completed. The dialog says
    // plainly that it is a Prospera-side cancel and to call the pharmacy.
    if (integrationKey === 'rmm') return !NON_CANCELLABLE.has(fs);
    if (!o.createdAt) return false;
    if (NON_CANCELLABLE.has(fs)) return false;
    // It reached the vendor: the short window is the vendor's grace period.
    return now - new Date(o.createdAt).getTime() <= CANCEL_WINDOW_MS;
  };

  if (isLoading) {
    return <div className="flex justify-center py-10"><Spinner /></div>;
  }

  if (orders.length === 0) {
    return <p className="py-8 text-center text-sm text-slate-500">{emptyMessage}</p>;
  }

  // Every order is cancelled and the switch is off — keep the toggle reachable
  // instead of showing a bare table-less empty state.
  if (visibleOrders.length === 0) {
    return (
      <div>
        {cancelledToggle}
        <p className="py-8 text-center text-sm text-slate-500">
          {cancelledCount === 1 ? '1 cancelled order is hidden.' : `${cancelledCount} cancelled orders are hidden.`}
        </p>
      </div>
    );
  }

  // Axiom (peptides) is a prescribed order, not a lab collection: it shows who
  // placed it and the prescriber, and drops the lab-oriented columns (order
  // date, results, billing).

  return (
    <div className="overflow-x-auto">
      {cancelledToggle}
      <table className="w-full text-sm">
        <thead>
          <tr className="border-b border-slate-200 text-left text-xs font-medium uppercase tracking-wide text-slate-500">
            <SortableHeader label="Order #" sortKey="orderNumber" sortBy={sortBy} sortDir={sortDir} onSort={toggleSort} />
            {showPatient && (
              <SortableHeader label="Patient" sortKey="patient" sortBy={sortBy} sortDir={sortDir} onSort={toggleSort} />
            )}
            {isAxiom && <th className="px-3 py-2">Vendor</th>}
            {showSource && <th className="px-3 py-2">Source</th>}
            <SortableHeader label="Ordered" sortKey="ordered" sortBy={sortBy} sortDir={sortDir} onSort={toggleSort} />
            <SortableHeader label="Product" sortKey="product" sortBy={sortBy} sortDir={sortDir} onSort={toggleSort} />
            {showRxColumns && (
              <SortableHeader label="Ordered by" sortKey="orderedBy" sortBy={sortBy} sortDir={sortDir} onSort={toggleSort} />
            )}
            {showRxColumns && (
              <SortableHeader label="Prescriber" sortKey="prescriber" sortBy={sortBy} sortDir={sortDir} onSort={toggleSort} />
            )}
            {showRxColumns && (
              <SortableHeader label="Day's supply" sortKey="supply" sortBy={sortBy} sortDir={sortDir} onSort={toggleSort} />
            )}
            {showRxColumns && (
              <SortableHeader label="Re-order" sortKey="reorder" sortBy={sortBy} sortDir={sortDir} onSort={toggleSort} />
            )}
            <th className="px-3 py-2" aria-sort={ariaSortFor(sortBy === 'status', sortDir)}>
              {statusHelp ? (
                // The sort control and the info trigger are SIBLINGS: the info
                // icon is itself a <button>, so wrapping the whole header in the
                // sort button would nest one button inside another.
                <span className="inline-flex items-center gap-1">
                  <SortButton label="Status" sortKey="status" sortBy={sortBy} sortDir={sortDir} onSort={toggleSort} />
                  <Tooltip>
                    <TooltipTrigger asChild>
                      <button type="button" tabIndex={-1} aria-label="What each status means" className="inline-flex text-slate-400 hover:text-slate-600">
                        <Info className="h-3.5 w-3.5" />
                      </button>
                    </TooltipTrigger>
                    <TooltipContent className="max-w-[300px]">
                      <div className="space-y-1 py-0.5 text-left normal-case tracking-normal">
                        {Object.entries(statusHelp).map(([code, desc]) => (
                          <p key={code} className="text-xs leading-snug">
                            <span className="font-semibold">{fulfillmentLabels[code] ?? code}</span>
                            <span className="font-normal"> — {desc}</span>
                          </p>
                        ))}
                      </div>
                    </TooltipContent>
                  </Tooltip>
                </span>
              ) : (
                <SortButton label="Status" sortKey="status" sortBy={sortBy} sortDir={sortDir} onSort={toggleSort} />
              )}
            </th>
            {isAxiom && (
              <th className="px-3 py-2">
                <span title="Payment · Reconstituted · Picked up (orders delivered to the clinic)">Clinic</span>
              </th>
            )}
            {!noResults && <th className="px-3 py-2">Results</th>}
            {!isAxiom && <th className="px-3 py-2">Billing</th>}
            <SortableHeader label="Amount" sortKey="amount" sortBy={sortBy} sortDir={sortDir} onSort={toggleSort} className="text-right" />
            {(isAxiom || canDebug || onEdit || onClone || onResend || onCancel || onFinishPayment || onReorder || onRetry) && <th className="px-3 py-2 text-right">Actions</th>}
          </tr>
        </thead>
        <tbody>
          {sortedOrders.map((o) => {
            const meta = o.metadata as Record<string, unknown> | null;
            const resultsReady = Boolean(meta?.resultsAvailable);
            const errorMsg = extractError(meta);
            const prescriber = orderPrescriber(o);
            return (
              <tr
                key={o.integrationOrderId}
                className="cursor-pointer border-b border-slate-100 hover:bg-slate-50"
                onClick={() => setDetailOrder(o)}
                title="View order details"
              >
                <td className="px-3 py-2 font-medium text-blue-600 hover:underline">#{o.integrationOrderId}</td>
                {showPatient && (
                  <td className="px-3 py-2 text-slate-700" onClick={(e) => e.stopPropagation()}>
                    {o.patientId ? (
                      <PatientLink patientId={o.patientId} patientName={o.patientName || `#${o.patientId}`}>
                        {o.patientName || `#${o.patientId}`}
                      </PatientLink>
                    ) : '—'}
                  </td>
                )}
                {isAxiom && (
                  <td className="px-3 py-2 text-slate-700">{o.vendorLabel ?? 'Axiom'}</td>
                )}
                {showSource && (
                  // Em dash for an untagged order. NOT defaulted to a channel:
                  // see the showSource prop docblock.
                  <td className="px-3 py-2 text-slate-700">{o.source || '—'}</td>
                )}
                <td className="px-3 py-2 whitespace-nowrap text-slate-600">
                  {o.createdAt ? (
                    <>
                      {new Date(o.createdAt).toLocaleDateString('en-US', { month: 'short', day: 'numeric', year: 'numeric' })}
                      <span className="block text-[11px] text-slate-400">
                        {new Date(o.createdAt).toLocaleTimeString('en-US', { hour: 'numeric', minute: '2-digit' })}
                      </span>
                    </>
                  ) : '—'}
                </td>
                <td className="px-3 py-2 text-slate-700">
                  {(() => {
                    // Live-catalog integrations (Genova, TruDiagnostic) order a generic
                    // default catalog product and carry the real test in metadata —
                    // prefer that so the table shows the actual test, not "Genova Diagnostics".
                    //
                    // `kitType` is NOT in that chain, and used to be. It is SiPhox's
                    // vendor code, not a product name: a Core Health kit reads
                    // EASY_DRAW_ORIGINAL, which looks like somebody ordered the wrong
                    // thing (SCRUM-1491 was filed as exactly that). The order was
                    // always right — the catalog product IS Core Health and the code
                    // IS its sku — so this only ever mislabelled it. The code still
                    // shows, underneath, because it is what SiPhox support asks for.
                    const realName = (meta?.genovaProductName ?? meta?.skuName) as string | undefined;
                    const kitCode = typeof meta?.kitType === 'string' && meta.kitType.trim()
                      ? meta.kitType.trim()
                      : null;
                    const li = meta?.lineItems as Array<{ name: string; quantity: number; brandName?: string }> | undefined;
                    // A prescription integration has no catalog lines in
                    // metadata — its drugs are the order's own rows. Without
                    // them this fell through to productName, which for Partell
                    // is one placeholder row literally named "Partell
                    // (RequestMyMeds)": every script in the table read as the
                    // vendor's name instead of the medicine.
                    const rx = o.items ?? [];
                    const body = realName ? realName : (
                      rx.length ? (
                        <div className="space-y-0.5">
                          {rx.map((x, i) => (
                            <p key={i} className="whitespace-nowrap">
                              {x.quantity}× {orderLineName(x.name)}
                            </p>
                          ))}
                        </div>
                      ) : (
                      // One item per line, each on a single line — the old
                      // comma-joined string wrapped mid-item and read run-together.
                      li && li.length ? (
                        <div className="space-y-0.5">
                          {li.map((x, i) => (
                            <p key={i} className="whitespace-nowrap">
                              {x.quantity}× {x.brandName ? `${x.brandName} — ${x.name}` : orderLineName(x.name)}
                            </p>
                          ))}
                        </div>
                      ) : (o.productName ?? '—')
                    ));
                    if (!kitCode) return body;
                    // The vendor code is NOT printed. Showing EASY_DRAW_ORIGINAL
                    // under "Core Health" is what SCRUM-1491 was filed about, and
                    // moving it to a second line did not answer that: staff still
                    // read the column and see a product they did not order. It
                    // stays reachable on hover for the one case that needs it —
                    // quoting the kit to SiPhox support.
                    return (
                      <span title={`Vendor kit code: ${kitCode}`}>{body}</span>
                    );
                  })()}
                </td>
                {showRxColumns && (
                  <td className="px-3 py-2 text-slate-700">{o.orderedBy ?? '—'}</td>
                )}
                {showRxColumns && (
                  <td className="px-3 py-2 text-slate-700">{prescriber ?? '—'}</td>
                )}
                {showRxColumns && (
                  <td className="px-3 py-2 whitespace-nowrap text-slate-700">
                    {(() => {
                      const days = supplyDays(o);
                      const estimated = supplyIsEstimated(o);
                      return (
                        <span
                          className={estimated ? 'text-slate-500' : ''}
                          title={
                            estimated
                              ? 'Estimated — this order carries no per-cartridge duration, so a one-month supply is assumed'
                              : `This order covers ${days} days`
                          }
                        >
                          {estimated ? '~' : ''}{days} days
                        </span>
                      );
                    })()}
                  </td>
                )}
                {showRxColumns && (
                  <td className="px-3 py-2 whitespace-nowrap text-slate-600">
                    {(() => {
                      const supersedingId = supersededBy.get(o.integrationOrderId);
                      if (supersedingId) {
                        const supersedingOrder = orderById.get(supersedingId) ?? chainOrderById.get(supersedingId);
                        return (
                          <button
                            type="button"
                            className="text-slate-500 hover:text-blue-600 hover:underline"
                            title={`Already re-ordered as #${supersedingId} — view it`}
                            onClick={(e) => {
                              e.stopPropagation();
                              if (supersedingOrder) setDetailOrder(supersedingOrder);
                            }}
                          >
                            Reordered
                            <span className="block text-[11px] text-slate-400">as #{supersedingId}</span>
                          </button>
                        );
                      }
                      const c = reorderCountdown(o, now);
                      if (!c) return '—';
                      return (
                        <>
                          <span className={c.due ? 'font-medium text-amber-700' : ''} title={c.due ? 'Supply is running low — time to re-order' : 'Estimated re-order date'}>
                            {c.date.toLocaleDateString('en-US', { month: 'short', day: 'numeric', year: 'numeric' })}
                          </span>
                          <span className={`block text-[11px] ${c.due ? 'text-amber-700' : 'text-slate-400'}`}>
                            {reorderPhrase(c.daysAway)}
                          </span>
                        </>
                      );
                    })()}
                  </td>
                )}
                <td className="px-3 py-2">
                  <Badge variant={fulfillmentVariant(o.fulfillmentStatus)}>
                    {fulfillmentLabels[o.fulfillmentStatus ?? ''] ?? (o.fulfillmentStatus ?? o.status)}
                  </Badge>
                  {errorMsg && (
                    <p className="mt-1 max-w-xs text-[11px] leading-snug text-red-600" title={errorMsg}>{errorMsg}</p>
                  )}
                  {extractShipments(meta, integrationKey, o.items).map((tracking) => {
                    const url = trackingUrl(tracking);
                    const label = `${tracking.carrier ? `${tracking.carrier} ` : ''}${tracking.trackingNumber}`;
                    // Each package's own date — what staff are asked first when a
                    // patient calls — never one package's date under another.
                    const dateLine = packageDateLine(tracking);
                    return (
                      <div key={tracking.trackingNumber}>
                        <p className="mt-1 flex items-center gap-1 text-[11px] leading-snug text-slate-600"
                          title={tracking.items?.length ? tracking.items.join(', ') : undefined}>
                          <Truck className="h-3 w-3 shrink-0" />
                          {url ? (
                            <a
                              href={url}
                              target="_blank"
                              rel="noreferrer"
                              className="inline-flex items-center gap-0.5 text-blue-600 hover:text-blue-800 hover:underline"
                              onClick={(e) => e.stopPropagation()}
                            >
                              {label}
                              <ExternalLink className="h-3 w-3" />
                            </a>
                          ) : (
                            <span>{label}</span>
                          )}
                        </p>
                        {dateLine && <p className="mt-0.5 pl-4 text-[11px] text-slate-500">{dateLine}</p>}
                      </div>
                    );
                  })}
                </td>
                {isAxiom && (
                  // Like Actions: a click in here never opens the row's details.
                  <td className="px-3 py-2" onClick={(e) => e.stopPropagation()}>
                    <HandoffChips order={o} controls={handoff} />
                  </td>
                )}
                {!noResults && (
                  <td className="px-3 py-2">
                    {resultsReady ? (
                      <span className="inline-flex items-center gap-2 text-xs font-medium text-emerald-700">
                        <span className="inline-flex items-center gap-1"><CheckCircle2 className="h-3.5 w-3.5" /> Ready</span>
                        {integrationKey === 'mescreen' && meta?.mescreenPdfReady === true && (
                          <button
                            type="button"
                            onClick={(e) => { e.stopPropagation(); void openMescreenPdf(o.patientId ?? patientId, o.integrationOrderId); }}
                            className="text-blue-600 hover:underline"
                          >
                            PDF
                          </button>
                        )}
                      </span>
                    ) : (
                      <span className="text-xs text-slate-400">—</span>
                    )}
                  </td>
                )}
                {!isAxiom && (
                  <td className="px-3 py-2 text-slate-600">{BILLING_MODE_LABELS[o.billingMode] ?? o.billingMode}</td>
                )}
                <td className="px-3 py-2 text-right font-medium text-slate-900">
                  {new Intl.NumberFormat('en-US', { style: 'currency', currency: o.currency || 'USD' }).format(o.amount)}
                </td>
                {(isAxiom || canDebug || onEdit || onClone || onResend || onCancel || onFinishPayment || onReorder || onRetry) && (
                  <td className="px-3 py-2 text-right" onClick={(e) => e.stopPropagation()}>
                    <div className="flex items-center justify-end gap-1.5">
                      {isAxiom && (() => {
                        // Axiom answered, or moved a ticket's status, since anyone
                        // last opened it. Written by the ticket webhook and cleared
                        // the moment the ticket is opened, so the dot means "there
                        // is something here you have not seen" — not "has tickets".
                        const alert = (o.metadata as { supportAlert?: { summary?: string } } | undefined)?.supportAlert;
                        return (
                          <IconAction
                            label={alert ? 'Axiom support — new update' : 'Axiom support'}
                            tip={alert?.summary
                              ? `New from Axiom: ${alert.summary}`
                              : 'Axiom support — view tickets for this order, or open a new one'}
                            onClick={() => { setSupportTicketId(null); setSupportOrder(o); }}
                          >
                            <span className="relative inline-flex">
                              <LifeBuoy className={`h-4 w-4 ${alert ? 'text-red-600' : ''}`} />
                              {alert && (
                                <span
                                  aria-hidden
                                  data-testid="support-alert-dot"
                                  className="absolute -right-0.5 -top-0.5 h-2 w-2 rounded-full bg-red-600 ring-2 ring-white"
                                />
                              )}
                            </span>
                          </IconAction>
                        );
                      })()}
                      {onFinishPayment && isAwaitingPayment(o) && (
                        <IconAction
                          label="Finish payment"
                          tip="Finish payment — this order is held until the fee is collected; nothing has been sent to the pharmacy"
                          onClick={() => onFinishPayment(o)}
                        >
                          <CreditCard className="h-4 w-4" />
                        </IconAction>
                      )}
                      {/*
                        Retry sits BEFORE re-order: on a failed row it is almost
                        always the action wanted, and the two are easy to confuse.
                        Paper plane (send it again) against re-order's cycle arrows.
                      */}
                      {onRetry && canRetry(o) && (
                        <IconAction
                          label="Retry"
                          tone="attention"
                          tip="Retry — re-send THIS order to the pharmacy. No new order and no second charge; use it once the reason it failed is fixed."
                          onClick={() => onRetry(o)}
                        >
                          <SendHorizonal className="h-4 w-4" />
                        </IconAction>
                      )}
                      {/*
                        Only a finished order (delivered / completed) can be
                        repeated, and only the LATEST one: an order already
                        re-ordered — by a newer order for the same patient and
                        product, or one placed from it with Re-order — never
                        offers it again, so order X re-ordered as Y leaves the
                        button on Y only. The due signal moves onto the button's
                        tone (and its wording). A cancelled order never offers
                        it, even when the pharmacy later reports it completed
                        (its webhook moves only fulfillmentStatus): that one
                        goes through Re-send.
                      */}
                      {onReorder && !isCancelled(o) && isReorderable(o) && !supersededBy.has(o.integrationOrderId) && (
                        <IconAction
                          label="Re-order"
                          tone={isReorderDue(o, now) ? 'attention' : 'default'}
                          tip={
                            isReorderDue(o, now)
                              ? 'Re-order — supply is running low; re-opens the order flow prefilled from this order'
                              : 'Re-order — place this order again; re-opens the order flow prefilled from it'
                          }
                          onClick={() => onReorder(o)}
                        >
                          <RefreshCw className="h-4 w-4" />
                        </IconAction>
                      )}
                      {canDebug && (
                        <IconAction
                          label="Debug"
                          tip="What Axiom was sent — above all, where this is shipping"
                          onClick={() => setDebugOrder(o)}
                        >
                          <Bug className="h-4 w-4" />
                        </IconAction>
                      )}
                      {/*
                        Re-send: a cancelled order can't be revived — the
                        pharmacy has closed it and its payment may already be
                        refunded — so it is placed again as a NEW order with the
                        same items and address, through the normal order form
                        (review, payment, stock and state checks all apply).
                      */}
                      {(onResend ?? onClone) && isCancelled(o) && (
                        <IconAction
                          label="Re-send"
                          tip="Re-send — place this cancelled order again with the same items and shipping address. Opens the order form to review; it goes out as a new order."
                          onClick={() => (onResend ?? onClone)!(o)}
                        >
                          <SendHorizonal className="h-4 w-4" />
                        </IconAction>
                      )}
                      {onClone && canClone(o) && (
                        <IconAction
                          label="Clone"
                          tip="Clone — place this order again, to the same address it was sent to"
                          onClick={() => onClone(o)}
                        >
                          <Copy className="h-4 w-4" />
                        </IconAction>
                      )}
                      {onEdit && canEdit(o) && (
                        <IconAction
                          label="Edit"
                          tip={
                            isAwaitingPayment(o)
                              ? 'Edit this order — items, prescriber and shipping address, before the payment is taken'
                              : 'Edit this order before it is sent to the pharmacy'
                          }
                          onClick={() => onEdit(o)}
                        >                          <Pencil className="h-4 w-4" />
                        </IconAction>
                      )}
                      {canCancel(o) && (
                        <IconAction
                          label="Cancel"
                          tone="danger"
                          tip={
                            isAwaitingPayment(o)
                              ? 'Cancel this order — it hasn’t been sent to the pharmacy yet'
                              : isAxiom
                                ? 'Cancel — the pharmacy allows cancellation only while the order is Received or Routed'
                                : `Cancel — orders can be cancelled within ${ORDER_CANCEL_WINDOW_MINUTES} minutes of creation`}
                          onClick={() => onCancel!(o)}
                        >
                          <Ban className="h-4 w-4" />
                        </IconAction>
                      )}
                    </div>
                  </td>
                )}
              </tr>
            );
          })}
        </tbody>
      </table>

      <Modal
        isOpen={detailOrder != null}
        onClose={() => setDetailOrder(null)}
        size="lg"
        title={detailOrder ? `Order #${detailOrder.integrationOrderId}` : ''}
      >
        {detailOrder && (
          <OrderDetailsModal
            // The live row, so a refetch after a handoff toggle reaches the modal.
            order={orderById.get(detailOrder.integrationOrderId) ?? detailOrder}
            fulfillmentLabels={fulfillmentLabels}
            integrationKey={integrationKey}
            patientId={detailOrder.patientId ?? patientId}
            handoff={isAxiom ? handoff : undefined}
          />
        )}
      </Modal>

      {isAxiom && (
        <Modal
          isOpen={supportOrder != null}
          onClose={() => { setSupportOrder(null); setSupportTicketId(null); }}
          size="md"
          title={supportOrder ? `Axiom Support — Order #${supportOrder.integrationOrderId}` : ''}
        >
          {supportOrder && (
            <AxiomSupportSection
              patientId={supportOrder.patientId ?? patientId}
              orderId={supportOrder.integrationOrderId}
              initialTicketId={supportTicketId}
              // Creating a ticket dismisses the whole stack: this modal and the
              // order-detail modal it can sit on top of.
              onCreated={() => {
                setSupportOrder(null);
                setSupportTicketId(null);
                setDetailOrder(null);
              }}
            />
          )}
        </Modal>
      )}

      {debugOrder && (
        <AxiomOrderDebugModal
          patientId={debugOrder.patientId ?? patientId}
          orderId={debugOrder.integrationOrderId}
          onClose={() => setDebugOrder(null)}
        />
      )}
    </div>
  );
}
